import type { ArdoqId, ViewIds } from '@ardoq/api-types';
import type { ViewContext } from '@ardoq/view-settings';
import type { CapturePlaceholderReason } from './slidePlan';

export type VisualizationSlideToCapture = { id: ArdoqId; view: ViewIds };

export type MetamodelSlideToCapture = { id: ArdoqId; metamodelId: ArdoqId };

export type ViewerSnapshot = {
  loadedSlideId: ArdoqId | null;
  hasSlideError: boolean;
  isLoading: boolean;
  mainViewId: ViewIds;
  viewPropertiesViewId: ViewIds;
  exportToPng: (() => Promise<void>) | null;
  isContainerConnected: boolean;
};

export type SlideReadiness =
  | { status: 'waiting' }
  | { status: 'ready'; exportToPng: () => Promise<void> }
  | { status: 'unready'; reason: CapturePlaceholderReason };

const WAITING: SlideReadiness = { status: 'waiting' };

const unready = (reason: CapturePlaceholderReason): SlideReadiness => ({
  status: 'unready',
  reason,
});

export const NOT_READY = unready('not-ready');

export type SlideSurface = {
  hasSlideError: boolean;
  isLoading: boolean;
  isShown: boolean;
  exportToPng: (() => Promise<void>) | null;
  isContainerConnected: boolean;
};

export const toSurfaceReadiness = (surface: SlideSurface): SlideReadiness => {
  if (surface.hasSlideError) {
    return unready('slide-load-failed');
  }
  if (
    surface.isLoading ||
    !surface.isShown ||
    !surface.exportToPng ||
    !surface.isContainerConnected
  ) {
    return WAITING;
  }
  return { status: 'ready', exportToPng: surface.exportToPng };
};

export const toFinalSurfaceReadiness = (
  surface: SlideSurface
): SlideReadiness => {
  const readiness = toSurfaceReadiness({ ...surface, isLoading: false });
  if (readiness.status !== 'waiting') {
    return readiness;
  }
  return !surface.isLoading && surface.isShown && !surface.exportToPng
    ? unready('no-exporter')
    : NOT_READY;
};

export const toSlideReadiness = (
  snapshot: ViewerSnapshot,
  { id, view }: VisualizationSlideToCapture
): SlideReadiness => {
  if (snapshot.hasSlideError) {
    return unready('slide-load-failed');
  }
  if (snapshot.loadedSlideId !== id || snapshot.isLoading) {
    return WAITING;
  }
  if (snapshot.mainViewId !== view || snapshot.viewPropertiesViewId !== view) {
    return WAITING;
  }
  if (!snapshot.exportToPng) {
    return unready('no-exporter');
  }
  if (!snapshot.isContainerConnected) {
    return WAITING;
  }
  return { status: 'ready', exportToPng: snapshot.exportToPng };
};

export const toFinalSlideReadiness = (
  snapshot: ViewerSnapshot,
  slide: VisualizationSlideToCapture
): SlideReadiness => {
  const readiness = toSlideReadiness({ ...snapshot, isLoading: false }, slide);
  if (
    readiness.status === 'unready' &&
    readiness.reason === 'no-exporter' &&
    snapshot.isLoading
  ) {
    return NOT_READY;
  }
  return readiness.status === 'waiting' ? NOT_READY : readiness;
};

export type ScenarioSlideToCapture = VisualizationSlideToCapture & {
  scenarioId: ArdoqId;
  isInVisualDiffMode: boolean;
};

export type ScenarioViewerSnapshot = Omit<
  ViewerSnapshot,
  'viewPropertiesViewId'
> & {
  activeScenarioId: ArdoqId | null;
  isInDiffMode: boolean;
  isUpdatingAppState: boolean;
};

const isScenarioViewShown = (
  snapshot: ScenarioViewerSnapshot,
  { id, view, scenarioId, isInVisualDiffMode }: ScenarioSlideToCapture
): boolean =>
  snapshot.loadedSlideId === id &&
  snapshot.mainViewId === view &&
  snapshot.activeScenarioId === scenarioId &&
  snapshot.isInDiffMode === isInVisualDiffMode &&
  !snapshot.isUpdatingAppState;

const toScenarioSurface = (
  snapshot: ScenarioViewerSnapshot,
  slide: ScenarioSlideToCapture
): SlideSurface => ({
  ...snapshot,
  isShown: isScenarioViewShown(snapshot, slide),
});

export const toScenarioSlideReadiness = (
  snapshot: ScenarioViewerSnapshot,
  slide: ScenarioSlideToCapture
): SlideReadiness => toSurfaceReadiness(toScenarioSurface(snapshot, slide));

export const toFinalScenarioSlideReadiness = (
  snapshot: ScenarioViewerSnapshot,
  slide: ScenarioSlideToCapture
): SlideReadiness =>
  toFinalSurfaceReadiness(toScenarioSurface(snapshot, slide));

export const isViewContextOf = (
  viewContext: ViewContext,
  viewInstanceId: string
): boolean => viewContext.viewInstanceId === viewInstanceId;

type ScenarioViewContextSources = {
  reported: ViewContext | null;
  modifiersContext: ViewContext;
  viewInstanceId: string;
};

export const toScenarioViewContext = ({
  reported,
  modifiersContext,
  viewInstanceId,
}: ScenarioViewContextSources): ViewContext | null =>
  reported ??
  (isViewContextOf(modifiersContext, viewInstanceId) ? modifiersContext : null);

export type ViewBusyByNamespace = Readonly<Record<string, boolean>>;

export type ViewLoadingEvent = { namespace: string; isBusy: boolean };

export type ViewBusyState = 'busy' | 'idle' | 'unseen';

export const recordViewLoading = (
  busyByNamespace: ViewBusyByNamespace,
  { namespace, isBusy }: ViewLoadingEvent
): ViewBusyByNamespace => ({ ...busyByNamespace, [namespace]: isBusy });

export const toViewBusyState = (
  busyByNamespace: ViewBusyByNamespace,
  viewInstanceId: string
): ViewBusyState => {
  if (!Object.hasOwn(busyByNamespace, viewInstanceId)) {
    return 'unseen';
  }
  return busyByNamespace[viewInstanceId] ? 'busy' : 'idle';
};

export type MetamodelSnapshot = {
  loadedSlideId: ArdoqId | null;
  hasSlideError: boolean;
  metamodelId: ArdoqId | null;
  graphMetamodelId: ArdoqId | null;
  exportToPng: (() => Promise<void>) | null;
  isContainerConnected: boolean;
};

const isMetamodelShown = (
  snapshot: MetamodelSnapshot,
  { id, metamodelId }: MetamodelSlideToCapture
): boolean =>
  snapshot.loadedSlideId === id && snapshot.metamodelId === metamodelId;

const toMetamodelSurface = (
  snapshot: MetamodelSnapshot,
  slide: MetamodelSlideToCapture
): SlideSurface => ({
  ...snapshot,
  isLoading: false,
  isShown: isMetamodelShown(snapshot, slide),
  exportToPng:
    snapshot.graphMetamodelId === slide.metamodelId
      ? snapshot.exportToPng
      : null,
});

export const toMetamodelReadiness = (
  snapshot: MetamodelSnapshot,
  slide: MetamodelSlideToCapture
): SlideReadiness => toSurfaceReadiness(toMetamodelSurface(snapshot, slide));

export const toFinalMetamodelReadiness = (
  snapshot: MetamodelSnapshot,
  slide: MetamodelSlideToCapture
): SlideReadiness =>
  toFinalSurfaceReadiness(toMetamodelSurface(snapshot, slide));

export type HtmlSlideToCapture = { id: ArdoqId };

export type HtmlSlideSnapshot = {
  loadedSlideId: ArdoqId | null;
  hasSlideError: boolean;
  surfaceSlideId: ArdoqId | null;
  isSurfaceBusy: boolean;
  hasSurfaceError: boolean;
  exportToPng: (() => Promise<void>) | null;
  isContainerConnected: boolean;
};

const isHtmlSurfaceShown = (
  snapshot: HtmlSlideSnapshot,
  { id }: HtmlSlideToCapture
): boolean =>
  snapshot.loadedSlideId === id &&
  snapshot.surfaceSlideId === id &&
  snapshot.isContainerConnected &&
  !snapshot.isSurfaceBusy;

export const toHtmlSlideReadiness = (
  snapshot: HtmlSlideSnapshot,
  slide: HtmlSlideToCapture
): SlideReadiness => {
  if (snapshot.hasSlideError) {
    return unready('slide-load-failed');
  }
  if (!isHtmlSurfaceShown(snapshot, slide)) {
    return WAITING;
  }
  if (snapshot.hasSurfaceError) {
    return unready('slide-load-failed');
  }
  if (!snapshot.exportToPng) {
    return WAITING;
  }
  return { status: 'ready', exportToPng: snapshot.exportToPng };
};

export const toFinalHtmlSlideReadiness = (
  snapshot: HtmlSlideSnapshot,
  slide: HtmlSlideToCapture
): SlideReadiness => {
  const readiness = toHtmlSlideReadiness(snapshot, slide);
  return readiness.status === 'waiting' ? NOT_READY : readiness;
};
